//
// # MultiInstanceSelector
//
// List selector for multiple instances in a constraint.
// Used by current mirror and matched load constraint types.
// Shows selected instances as removable chips with an add dropdown.
//

import React from "react";
import { X } from "lucide-react";

export interface MultiInstanceSelectorProps {
  readonly label: string;
  readonly instances: string[];
  readonly setInstances: (v: string[]) => void;
  readonly available: string[];
  readonly formatInstance: (name: string) => string;
  readonly inputClass: string;
  readonly inputStyle: React.CSSProperties;
  readonly labelClass: string;
  readonly labelStyle: React.CSSProperties;
}

export function MultiInstanceSelector({
  label,
  instances,
  setInstances,
  available,
  formatInstance,
  inputClass,
  inputStyle,
  labelClass,
  labelStyle,
}: MultiInstanceSelectorProps) {
  const handleAdd = (name: string) => {
    if (name && !instances.includes(name)) {
      setInstances([...instances, name]);
    }
  };

  const handleRemove = (name: string) => {
    setInstances(instances.filter((n: string) => n !== name));
  };

  return (
    <div>
      <label className={labelClass} style={labelStyle}>
        {label}
      </label>
      {/* Instance list with remove buttons */}
      {instances.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {instances.map((name: string) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs"
              style={{
                backgroundColor: "var(--color-ide-surface)",
                border: "1px solid var(--color-ide-border)",
                color: "var(--color-ide-text)",
              }}
            >
              {formatInstance(name)}
              <button
                type="button"
                onClick={() => handleRemove(name)}
                className="hover:opacity-70"
                style={{ color: "var(--color-ide-text-muted)" }}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Add dropdown */}
      <select
        value=""
        onChange={(e) => {
          handleAdd(e.target.value);
        }}
        className={inputClass}
        style={inputStyle}
      >
        <option value="">— Add instance —</option>
        {available.map((name: string) => (
          <option key={name} value={name}>
            {formatInstance(name)}
          </option>
        ))}
      </select>
    </div>
  );
}
